import React from 'react';
import { Dropdown } from 'antd';
import { Notification } from 'services';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import ArrowSquareLeft from '../dashboardV3/arrow-square-left.svg';
import ArrowSquareRight from '../dashboardV3/arrow-square-right.svg';
import { getInstallmentBadgeInfo, getInstallmentNumber, formatFeeDate } from './feeUtils';
import FeePaymentSidebar from './FeePaymentSidebar';

const FeeInstallmentPlan = ({
    currentFee,
    courseName,
    batchInfo,
    totalFee,
    totalPaid,
    totalPending,
    nextDueLabel,
    percentPaid,
    overdueCount,
    sortedInstallments,
    activeFeeIndex,
    feeListLength,
    onPrevFee,
    onNextFee,
    paymentModeSplit,
    chartSegments,
    splitTotal,
    defaultSplitColors,
    discountsList,
    onDownloadInvoice,
    onCancelRequestClick,
}) => {
    return (
        <div style={{ display: "flex", gap: fluid(24), alignItems: "stretch", flex: 1, minHeight: 0 }}>
            {/* Left Scrollable Column */}
            <div
                className="staff-profile-info-scroll"
                style={{
                    flex: `1 1 ${fluid(882)}`,
                    minWidth: fluid(882),
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(24),
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                }}
            >
                {/* Course Info Card */}
                <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(20) }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(16) }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                                {courseName}
                            </span>
                            <div style={{ display: "flex", gap: fluid(6) }}>
                                <img
                                    src={ArrowSquareLeft}
                                    alt="Prev"
                                    onClick={onPrevFee}
                                    style={{ width: fluid(32), height: fluid(32), cursor: activeFeeIndex > 0 ? "pointer" : "default", opacity: activeFeeIndex > 0 ? 1 : 0.4 }}
                                />
                                <img
                                    src={ArrowSquareRight}
                                    alt="Next"
                                    onClick={onNextFee}
                                    style={{ width: fluid(32), height: fluid(32), cursor: activeFeeIndex < feeListLength - 1 ? "pointer" : "default", opacity: activeFeeIndex < feeListLength - 1 ? 1 : 0.4 }}
                                />
                            </div>
                        </div>
                        {batchInfo && (
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666", fontWeight: 400 }}>
                                {batchInfo}
                            </span>
                        )}
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666" }}>Course fee</span>
                        <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: fluid(22),
                            padding: `0 ${fluid(10)}`,
                            borderRadius: fluid(4),
                            backgroundColor: "#F0F9FF",
                            border: "1px solid #7DCDFF",
                            color: "#008BE3",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: fluid(12),
                            fontWeight: 600,
                            lineHeight: fluid(18),
                            boxSizing: "border-box",
                            whiteSpace: "nowrap"
                        }}>
                            Classroom course
                        </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "baseline", gap: fluid(12), marginTop: `calc(${fluid(12)} * -1)` }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(24), fontWeight: 600, color: "#191919" }}>
                            ₹{totalFee.toLocaleString()}
                        </span>
                    </div>

                    {/* Stat Summary Row */}
                    <div style={{ display: "flex", gap: fluid(16) }}>
                        {[
                            { label: "Paid", val: `₹${totalPaid.toLocaleString()}` },
                            { label: "Pending", val: `₹${totalPending.toLocaleString()}` },
                            { label: "Next due", val: nextDueLabel },
                        ].map((box, idx) => (
                            <div key={idx} style={{ flex: 1, backgroundColor: "#F4F4F4", borderRadius: fluid(16), padding: fluid(16), height: fluid(77), boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666" }}>{box.label}</span>
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 500 }}>{box.val}</span>
                            </div>
                        ))}
                    </div>

                    {/* Progress bar */}
                    <div style={{ display: "flex", flexDirection: "column", gap: fluid(8), marginTop: fluid(12) }}>
                        <div style={{ width: "100%", height: fluid(8), borderRadius: fluid(100), backgroundColor: "#E5E7EB", position: "relative", overflow: "hidden" }}>
                            <div style={{ width: `${percentPaid}%`, height: "100%", borderRadius: fluid(100), backgroundColor: "#21A684", transition: "width 0.3s ease" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666" }}>{percentPaid}% paid</span>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666" }}>Overdue: {overdueCount}</span>
                        </div>
                    </div>
                </div>

                {/* Installment Schedule Card */}
                <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(20) }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                            Installment schedule
                        </span>
                        <span
                            onClick={() => Notification.info("Record offline payment", "Payment recording functionality is coming soon.")}
                            style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), fontWeight: 700, color: "#594099", cursor: "pointer", letterSpacing: "0.5px" }}
                        >
                            RECORD OFFLINE PAYMENT
                        </span>
                    </div>

                    {sortedInstallments.length === 0 ? (
                        <div style={{ padding: fluid(24), textAlign: "center", color: "#888888", fontFamily: "Roboto, sans-serif", fontSize: fluid(15) }}>
                            No installment schedule specified for this course.
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {sortedInstallments.map((inst, idx) => {
                                const badge = getInstallmentBadgeInfo(inst);
                                const rawStatus = (inst?.status || "").toLowerCase().trim();
                                const badgeText = (badge?.text || "").toLowerCase().trim();

                                const isCancelledOrRefund =
                                    rawStatus === "cancelled" ||
                                    rawStatus === "rejected" ||
                                    rawStatus === "refund" ||
                                    rawStatus === "refunded" ||
                                    badgeText === "cancelled" ||
                                    badgeText === "rejected" ||
                                    badgeText === "refund" ||
                                    badgeText === "refunded";

                                const isPaid =
                                    !isCancelledOrRefund && (
                                        rawStatus === "paid" ||
                                        rawStatus === "settled" ||
                                        badgeText === "paid" ||
                                        badgeText === "settled"
                                    );

                                const isPendingDueOrOverdue = !isCancelledOrRefund && !isPaid;

                                const subText = inst.subtitle || (() => {
                                    if (isPaid) {
                                        const methodStr = inst.paymentMethod ? `Paid via ${inst.paymentMethod}` : 'Paid';
                                        const refStr = inst.transactionId ? ` · Ref TXN${inst.transactionId}` : (inst.cardInfo ? ` · ${inst.cardInfo}` : (inst.invoiceNo ? ` · Inv #${inst.invoiceNo}` : ''));
                                        const dateStr = formatFeeDate(inst.paymentDate || inst.dueDate);
                                        return `${methodStr}${refStr}${dateStr ? ` | ${dateStr}` : ''}`;
                                    }
                                    const remindersStr = inst.remindersCount ? `${inst.remindersCount} reminders sent | ` : '';
                                    const dateStr = inst.dueDate ? formatFeeDate(inst.dueDate) : 'Due date not set';
                                    return `${remindersStr}${dateStr}`;
                                })();

                                const displayNumber = getInstallmentNumber(inst, sortedInstallments.length - idx);
                                const installmentTitle = (inst.name || `Installment ${displayNumber}`).replace(/\s+-\s+/, ' · ');

                                return (
                                    <div key={idx} style={{ display: "flex", flexDirection: "column", width: "100%", marginTop: idx > 0 ? fluid(16) : "0px" }}>
                                        <div style={{ display: "flex", alignItems: "center", height: fluid(50), width: "100%" }}>
                                            {/* Number container */}
                                            <div style={{ width: fluid(44), height: fluid(44), borderRadius: fluid(10), backgroundColor: "#D8F0FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#191919", fontWeight: 500 }}>{displayNumber}</span>
                                            </div>

                                            {/* Text block */}
                                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: fluid(44), marginLeft: fluid(16), flex: 1, minWidth: 0 }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {installmentTitle}
                                                </span>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#666666", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {subText}
                                                </span>
                                            </div>

                                            {/* Amount and Badge */}
                                            <div style={{ display: "flex", alignItems: "center", flexShrink: 0, marginLeft: fluid(16) }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 600, minWidth: fluid(80), textAlign: "right", marginRight: fluid(24) }}>
                                                    ₹{Number(inst.amountPayable ?? inst.amount ?? 0).toLocaleString()}
                                                </span>

                                                <div style={{ width: fluid(94), display: "flex", justifyContent: "flex-end" }}>
                                                    <span style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        height: fluid(22),
                                                        padding: `0 ${fluid(10)}`,
                                                        borderRadius: fluid(4),
                                                        backgroundColor: badge.bg,
                                                        border: `1px solid ${badge.border}`,
                                                        color: badge.textCol,
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: fluid(12),
                                                        fontWeight: 600,
                                                        lineHeight: fluid(18),
                                                        boxSizing: "border-box",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {badge.text}
                                                    </span>
                                                </div>

                                                {/* 3-dots Dropdown */}
                                                {isCancelledOrRefund ? (
                                                    <div style={{ width: fluid(26), marginLeft: fluid(16) }} />
                                                ) : (
                                                    <Dropdown
                                                        overlay={
                                                            <div style={{
                                                                backgroundColor: "#FFFFFF",
                                                                borderRadius: fluid(12),
                                                                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                                                                padding: `${fluid(6)} 0`,
                                                                minWidth: fluid(165),
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                overflow: "hidden"
                                                            }}>
                                                                {isPaid && (
                                                                    <>
                                                                        <div
                                                                            onClick={() => onDownloadInvoice(inst)}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#F8F8F8"}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                                                            style={{
                                                                                padding: `${fluid(10)} ${fluid(16)}`,
                                                                                fontSize: fluid(15),
                                                                                fontFamily: "Roboto, sans-serif",
                                                                                color: "#4A5568",
                                                                                cursor: "pointer",
                                                                                transition: "background-color 0.15s ease"
                                                                            }}
                                                                        >
                                                                            Download invoice
                                                                        </div>
                                                                        <div
                                                                            onClick={() => Notification.info("Initiate refund", "Initiating refund for this installment...")}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#FFFBEB"}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                                                            style={{
                                                                                padding: `${fluid(10)} ${fluid(16)}`,
                                                                                fontSize: fluid(15),
                                                                                fontFamily: "Roboto, sans-serif",
                                                                                color: "#D97706",
                                                                                cursor: "pointer",
                                                                                transition: "background-color 0.15s ease"
                                                                            }}
                                                                        >
                                                                            Initiate refund
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {isPendingDueOrOverdue && (
                                                                    <div
                                                                        onClick={() => onCancelRequestClick(inst)}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#FEF2F2"}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                                                        style={{
                                                                            padding: `${fluid(10)} ${fluid(16)}`,
                                                                            fontSize: fluid(15),
                                                                            fontFamily: "Roboto, sans-serif",
                                                                            color: "#DC2626",
                                                                            cursor: "pointer",
                                                                            transition: "background-color 0.15s ease"
                                                                        }}
                                                                    >
                                                                        Cancel request
                                                                    </div>
                                                                )}
                                                            </div>
                                                        }
                                                        trigger={['click']}
                                                        placement="bottomRight"
                                                    >
                                                        <div
                                                            style={{
                                                                marginLeft: fluid(16),
                                                                cursor: "pointer",
                                                                color: "#666666",
                                                                fontSize: fluid(18),
                                                                display: "flex",
                                                                alignItems: "center",
                                                                padding: fluid(4)
                                                            }}
                                                        >
                                                            ⋮
                                                        </div>
                                                    </Dropdown>
                                                )}
                                            </div>
                                        </div>

                                        {/* Divider */}
                                        {idx < sortedInstallments.length - 1 && (
                                            <div style={{ width: "100%", height: "1px", backgroundColor: "#E6E6E6", marginTop: fluid(16) }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Column */}
            <FeePaymentSidebar
                totalPaid={totalPaid}
                paymentModeSplit={paymentModeSplit}
                chartSegments={chartSegments}
                splitTotal={splitTotal}
                defaultSplitColors={defaultSplitColors}
                currentFee={currentFee}
                discountsList={discountsList}
            />
        </div>
    );
};

export default FeeInstallmentPlan;
